import { Alert, Snackbar } from '@mui/material';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { RequireSignInLevel } from '../abelcommon/hooks/useElevationSignInCheck';
import { appVersionState, env, globalAlertState, persistAppVersion } from '../api';
import { PBox } from '../components/CenterBox';
import { MustLogoutRoute } from '../components/RedirectRoute';
import { AccountSettingPage } from './Account/AccountSettingPage';
import { MachinePage } from './Account/MachinePage/MachinePage';
import { RewardPage } from './Account/RewardPage';
import { ForgotPasswordPage } from './Auth/ForgotPasswordPage';
import { LoginPage } from './Auth/LoginPage';
import { RegisterPage } from './Auth/RegisterPage';
import Dashboard from './Dashboard';
import Download from './Download';
import { HashRateCalculatorPage } from './Home/HashRateCalculatorPage';
import Home from './Home/HomePage';
import { MinedBlockPage } from './Home/MinedBlockPage';
import Layout from './Layout';
import Analytics from './Analytics/Analytics';
import ContentManagement from './ContentManagement/ContentManagement';
import TwoFactorAuthPage from './TwoFactorAuthModal/Modal2FAManager';
import QuickStartGuide from './QuickstartGuide/QuickstartGuide';
import { useTranslation } from 'react-i18next';

export function App() {
  const [alertState, setAlertState] = useRecoilState(globalAlertState);
  const appVersion = useRecoilValue(appVersionState);
  const { t } = useTranslation();

  useEffect(() => {
    document.title = env.title;
  }, [t]);

  useEffect(() => {
    if (appVersion.isStale) {
      persistAppVersion(appVersion.appVersion);
      window.location.reload();
    }
  }, [appVersion]);

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path={'/account'} element={<Layout requireSignInLevel={RequireSignInLevel.User} />}>
            <Route path={'settings'} element={<AccountSettingPage />} />
          </Route>
          <Route path={'/account'} element={<Layout requireSignInLevel={RequireSignInLevel.MiningAccount} />}>
            <Route index element={<Navigate to={'dashboard'} replace />} />
            <Route path={'dashboard'} element={<RewardPage />} />
            <Route path={'pool/:poolname'} element={<Dashboard />} />
            <Route path={'machine'} element={<MachinePage />} />
            <Route path={'*'} element={<PBox />} />
          </Route>
          <Route path={'/'} element={<Layout requireSignIn={false} />}>
            <Route index element={<Navigate to={'home'} replace />} />
            <Route path={'home'}>
              <Route index element={<Home />} />
              <Route path={'guide'} element={<QuickStartGuide />} />
              <Route path={'hash-rate-calculator'} element={<HashRateCalculatorPage />} />
              <Route path={'mined-list'} element={<MinedBlockPage />} />
            </Route>
            <Route path={'/auth'}>
              <Route index element={<Navigate to={'login'} replace />} />
              <Route
                path={'login'}
                element={
                  <MustLogoutRoute>
                    <LoginPage />
                  </MustLogoutRoute>
                }
              />
              <Route
                path={'register'}
                element={
                  <MustLogoutRoute>
                    <RegisterPage />
                  </MustLogoutRoute>
                }
              />
              <Route
                path={'forgot-password'}
                element={
                  <MustLogoutRoute>
                    <ForgotPasswordPage />
                  </MustLogoutRoute>
                }
              />
            </Route>
            <Route path={'download'} element={<Download />} />
            <Route path={'*'} element={<PBox />} />
            <Route path={'analytics'} element={<Analytics />} />
            <Route path={'content-management'} element={<ContentManagement />} />
            <Route path="/two-factor-auth" element={<TwoFactorAuthPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Snackbar
        open={alertState.isOpen}
        autoHideDuration={alertState.autoHideDuration}
        onClose={(e, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setAlertState((prev) => ({ ...prev, isOpen: false }));
        }}
      >
        <Alert
          onClose={() => setAlertState((prev) => ({ ...prev, isOpen: false }))}
          severity={alertState.severity}
          variant="filled"
          sx={{ width: '100%', color: 'white' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default App;
